/**
 * @fileoverview StageManager - Manages Konva stage and layers.
 * Handles stage creation, configuration, resizing, and layer management.
 */

import Konva from 'konva';
import { CONFIG } from './GameConfig.js';

export interface StageConfig {
    containerId: string;
    width: number;
    height: number;
    pixelRatio?: number;
}

/**
 * Manages the Konva stage and layers for the game.
 * Responsible for stage lifecycle, performance optimizations, and layer organization.
 */
export class StageManager {
    private stage: Konva.Stage | null = null;
    private staticLayer: Konva.Layer | null = null;
    private dragLayer: Konva.Layer | null = null;
    private containerId: string;
    
    constructor(containerId: string = 'container') {
        this.containerId = containerId;
    }
    
    /**
     * Initialize the stage with performance optimizations.
     * Creates stage with specified dimensions and applies pixelRatio=1 for old device compatibility.
     */
    init(config?: Partial<StageConfig>): void {
        // Force low pixel ratio for performance on old devices
        Konva.pixelRatio = config?.pixelRatio ?? 1;
        
        this.stage = new Konva.Stage({
            container: config?.containerId || this.containerId,
            width: config?.width || CONFIG.WIDTH,
            height: config?.height || CONFIG.HEIGHT
        });
        
        // Create separate layers for performance optimization
        this.createLayers();
    }
    
    /**
     * Create separate layers for performance optimization.
     * Static layer for targets/UI, drag layer for active dragging.
     * Multi-layer strategy improves performance on old devices.
     */
    private createLayers(): void {
        if (!this.stage) {
            throw new Error('Stage must be initialized before creating layers');
        }
        
        // Static layer for targets, UI, and feedback
        this.staticLayer = new Konva.Layer();
        this.stage.add(this.staticLayer);
        
        // Drag layer for shapes being actively dragged
        this.dragLayer = new Konva.Layer();
        this.stage.add(this.dragLayer);
    }
    
    /**
     * Get the Konva stage instance.
     */
    getStage(): Konva.Stage {
        if (!this.stage) {
            throw new Error('Stage not initialized. Call init() first');
        }
        return this.stage;
    }
    
    /**
     * Get the static layer (for targets, UI, feedback).
     */
    getStaticLayer(): Konva.Layer {
        if (!this.staticLayer) {
            throw new Error('Static layer not initialized. Call init() first');
        }
        return this.staticLayer;
    }
    
    /**
     * Get the drag layer (for shapes being dragged).
     */
    getDragLayer(): Konva.Layer {
        if (!this.dragLayer) {
            throw new Error('Drag layer not initialized. Call init() first');
        }
        return this.dragLayer;
    }
    
    /**
     * Resize the stage to new dimensions.
     * Updates both stage size and CONFIG dimensions.
     */
    resize(width: number, height: number): void {
        if (!this.stage) {
            throw new Error('Stage not initialized. Cannot resize');
        }
        
        this.stage.width(width);
        this.stage.height(height);
        
        // Update CONFIG to match new dimensions
        CONFIG.WIDTH = width;
        CONFIG.HEIGHT = height;
        
        // Batch draw both layers for performance
        this.staticLayer?.batchDraw();
        this.dragLayer?.batchDraw();
    }
    
    /**
     * Clear all children from both layers.
     * Useful for game reset without destroying the stage.
     */
    clearLayers(): void {
        this.staticLayer?.destroyChildren();
        this.dragLayer?.destroyChildren();
    }
    
    /**
     * Batch draw both layers.
     * More performant than individual draw() calls.
     */
    batchDraw(): void {
        this.staticLayer?.batchDraw();
        this.dragLayer?.batchDraw();
    }
    
    /**
     * Stop any active dragging on the stage.
     * Useful when pausing the game.
     */
    stopDrag(): void {
        this.stage?.stopDrag();
    }
    
    /**
     * Check if stage is initialized.
     */
    isInitialized(): boolean {
        return this.stage !== null && this.staticLayer !== null && this.dragLayer !== null;
    }
    
    /**
     * Destroy the stage and clean up resources.
     * Call this when completely shutting down the game.
     */
    destroy(): void {
        if (this.stage) {
            this.stage.destroy();
            this.stage = null;
            this.staticLayer = null;
            this.dragLayer = null;
        }
    }
}
